/**
 * @file index.tsx
 *
 * @fileoverview The page for the job selection type
 */

import { GetServerSideProps, NextPage } from 'next';
import React from 'react';
import JobWizardStepType from '~/jobiqo/components/Job/Wizard/Steps/AddJobType';
import { withoutAuthenticateApplicant } from '~/jobiqo/hocs/withoutAuthenticate/applicant';
import { JobWizardContextProvider } from '~/jobiqo/components/Job/Wizard/context/useJobWizardContext';
import { gsspRedirect } from '~/jobiqo/lib/redirect';
import GET_JOB_BUNDLES from '~/jobiqo/components/Job/Wizard/Steps/Bundles/graphql/queries/jobBundles.gql';
import initApollo from '~/jobiqo/apollo/initApollo';
import { getBundleFromQueryParams } from '~/jobiqo/components/Job/Wizard/utils';
import { JobBundle } from '~/jobiqo/models/jobs';
import { sortByWeight } from '~/jobiqo/utils/sort';
import GET_DEFAULT_PRODUCT from '~/jobiqo/pages/job/wizard/bundles/graphql/queries/getDefaultProduct.gql';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';

interface JobWizardJobTypePageProps {
  allowedJobTypes: string[];
}
const JobWizardJobTypePage: NextPage<JobWizardJobTypePageProps> = ({
  allowedJobTypes,
}) => (
  <JobWizardContextProvider>
    <JobWizardStepType allowedJobTypes={allowedJobTypes} />
  </JobWizardContextProvider>
);

export const getServerSideProps: GetServerSideProps = withoutAuthenticateApplicant(
  {
    gssp: async ({ query, resolvedUrl, ...ctx }) => {
      const frontendConfig = getServerFrontendConfig();
      const queryParams = resolvedUrl.split('?')?.[1] ?? null;
      let jobType = query.jobType?.toString() || 'job_per_template';
      let allowedJobTypes = frontendConfig.JOBS.ALLOWED_JOB_TYPES;
      const bundleId = getBundleFromQueryParams(resolvedUrl);
      let jobBundles: JobBundle[] = [];

      const apolloClient = initApollo({}, ctx);

      const { error, data } = await apolloClient.query({
        query: GET_JOB_BUNDLES,
        variables: {
          locale: ctx.locale,
        },
      });

      const {
        error: defaultProductError,
        data: defaultProductData,
      } = await apolloClient.query({
        query: GET_DEFAULT_PRODUCT,
        variables: {
          language: ctx.locale,
        },
      });

      if (!error && data?.jobBundles) {
        jobBundles = Array.from(data.jobBundles);
        jobBundles.sort(sortByWeight);
      }

      // if the current bundle has a limit on job types make sure to change allowed job types accordingly.
      if (bundleId && jobBundles.length > 0) {
        const bundle = jobBundles.find((bundle) => bundle.id === bundleId);

        if (bundle && (bundle.allowedJobTypes?.length ?? 0) > 0) {
          const bundleAllowedJobTypes = bundle.allowedJobTypes!.map((jobType) =>
            jobType?.toLowerCase(),
          );
          // Intersect with the config's allowed job types.
          allowedJobTypes = bundleAllowedJobTypes.filter((type) =>
            allowedJobTypes.includes(type),
          );
        }
      }

      // If no job bundle is selected and the default product is being used and its itself a bundle,
      // check also if it has allowed job types (special field in job bundles only)
      // and changed allowedJobTypes accordingly.
      if (
        !bundleId &&
        !defaultProductError &&
        defaultProductData?.getDefaultProduct?.type === 'job_bundle' &&
        defaultProductData?.getDefaultProduct?.allowedJobTypes?.length > 0
      ) {
        const bundleAllowedJobTypes = defaultProductData.getDefaultProduct.allowedJobTypes.map(
          (jobType) => jobType.toLowerCase(),
        );
        // Intersect with the config's allowed job types.
        allowedJobTypes = bundleAllowedJobTypes.filter((type) =>
          allowedJobTypes.includes(type),
        );
      }

      // If we have only one job type redirect directly to it.
      if (allowedJobTypes.length === 1) {
        return {
          props: {},
          redirect: {
            destination: gsspRedirect(
              ctx,
              `/job/wizard/form?jobType=${allowedJobTypes[0]}${
                queryParams ? '&' + queryParams : ''
              }`,
            ),
            permanent: false,
          },
        };
      }

      // gracefully fallback to job per template.
      // We can't reuse the "type" from graphql because its a union not a enum.
      if (
        !['job_per_template', 'job_per_link', 'job_per_file'].includes(jobType)
      ) {
        jobType = 'job_per_template';
      }

      return {
        props: {
          jobType,
          allowedJobTypes,
        },
      };
    },
  },
);

export default JobWizardJobTypePage;
